import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Briefcase, MapPin, Clock, Mail, ArrowRight, Users, Star, Trophy, Building2,
  Award, Calendar, Zap, Search, ChevronRight, Sparkles, Shield, Heart,
  CheckCircle, Globe, Target, Layers, Filter, Send, LogIn, UserPlus, Lock,
  Bookmark, BookmarkCheck
} from 'lucide-react';
import { contactInfo } from '../mock';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import VacancyApplicationModal from '../components/VacancyApplicationModal';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BENEFITS = [
  { icon: Users, title: "Team Culture", desc: "Join a passionate, close-knit team that celebrates wins together", color: '#00E5FF' },
  { icon: Target, title: "Real Growth", desc: "Clear progression paths and mentorship from day one", color: '#8A2BE2' },
  { icon: Trophy, title: "Recognition", desc: "Your contributions are valued and publicly celebrated", color: '#F59E0B' },
  { icon: Globe, title: "Diverse Ventures", desc: "Work across multiple brands and exciting projects", color: '#10B981' },
  { icon: Heart, title: "Wellbeing First", desc: "Dedicated tools and support for your mental health", color: '#EF4444' },
  { icon: Shield, title: "Job Security", desc: "Stable roles in a growing, established organisation", color: '#3B82F6' },
];

const Vacancies = () => {
  const navigate = useNavigate();
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVacancy, setSelectedVacancy] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [applyingFor, setApplyingFor] = useState(null);
  const [authPromptFor, setAuthPromptFor] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [bookmarks, setBookmarks] = useState(new Set());
  const [quickReady, setQuickReady] = useState(null);
  const [quickBusy, setQuickBusy] = useState(null);

  const fetchQuickReady = async () => {
    const token = localStorage.getItem('user_token');
    if (!token) { setQuickReady({ ready: false }); return; }
    try {
      const r = await axios.get(`${BACKEND_URL}/api/users/me/quick-apply/ready`,
        { headers: { Authorization: `Bearer ${token}` } });
      setQuickReady(r.data);
    } catch { setQuickReady({ ready: false }); }
  };

  const runQuickApply = async (vacancy) => {
    const token = localStorage.getItem('user_token');
    if (!token) { requestApply(vacancy); return; }
    const id = vacancy.id || vacancy._id;
    setQuickBusy(id);
    try {
      await axios.post(`${BACKEND_URL}/api/users/me/quick-apply/${id}`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Applied — good luck!', { description: `We'll email you as soon as we review it.` });
      // Refresh readiness (mostly for the "already applied" case next time)
      fetchQuickReady();
    } catch (err) {
      const msg = err.response?.data?.detail || 'One-click apply failed';
      // Fall back to the full form for anything the quick path can't handle
      if (err.response?.status === 400 || err.response?.status === 404) {
        toast.error(msg);
      } else if (err.response?.status === 409) {
        toast.info(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setQuickBusy(null);
    }
  };

  useEffect(() => {
    fetchVacancies();
    const token = localStorage.getItem('user_token');
    if (token) {
      axios.get(`${BACKEND_URL}/api/users/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setCurrentUser(r.data))
        .catch(() => setCurrentUser(null));
      axios.get(`${BACKEND_URL}/api/users/me/vacancy-bookmarks`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setBookmarks(new Set(r.data.vacancy_ids || [])))
        .catch(() => {});
      fetchQuickReady();
    }
  }, []);

  // Resume the apply flow when a user returns after signing in / signing up
  useEffect(() => {
    if (!currentUser || vacancies.length === 0) return;
    let resumeId;
    try { resumeId = sessionStorage.getItem('resume_apply_vacancy_id'); } catch {}
    if (!resumeId) return;
    const match = vacancies.find(v => (v.id || v._id) === resumeId);
    if (match) {
      setApplyingFor(match);
      toast.success('Welcome back — pick up where you left off.');
    }
    try { sessionStorage.removeItem('resume_apply_vacancy_id'); } catch {}
  }, [currentUser, vacancies]);

  const requestApply = (vacancy) => {
    const token = localStorage.getItem('user_token');
    if (!token) {
      // Store intent so we can resume after login
      try { sessionStorage.setItem('resume_apply_vacancy_id', vacancy.id || vacancy._id || ''); } catch {}
      setAuthPromptFor(vacancy);
      return;
    }
    setApplyingFor(vacancy);
  };

  const toggleBookmark = async (vacancy) => {
    const token = localStorage.getItem('user_token');
    if (!token) {
      try { sessionStorage.setItem('resume_apply_vacancy_id', vacancy.id || vacancy._id || ''); } catch {}
      setAuthPromptFor(vacancy);
      return;
    }
    const id = vacancy.id || vacancy._id;
    // Optimistic
    const wasBookmarked = bookmarks.has(id);
    setBookmarks(prev => {
      const next = new Set(prev);
      if (wasBookmarked) next.delete(id); else next.add(id);
      return next;
    });
    try {
      const r = await axios.post(`${BACKEND_URL}/api/users/me/vacancy-bookmarks/${id}`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(r.data.bookmarked ? 'Saved — we\'ll ping you before it closes' : 'Removed from saved roles');
    } catch (err) {
      // Rollback
      setBookmarks(prev => {
        const next = new Set(prev);
        if (wasBookmarked) next.add(id); else next.delete(id);
        return next;
      });
      toast.error('Could not update bookmark');
    }
  };

  const fetchVacancies = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/vacancies`);
      setVacancies(response.data);
    } catch (error) {
      console.error('Error fetching vacancies:', error);
    } finally {
      setLoading(false);
    }
  };

  const departments = ['all', ...new Set(vacancies.map(v => v.department).filter(Boolean))];

  const filteredVacancies = vacancies.filter(v => {
    const matchesDept = activeFilter === 'all' || v.department === activeFilter;
    const matchesSearch = v.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         v.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         v.department?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesDept && matchesSearch;
  });

  const openDetails = (vacancy) => {
    setSelectedVacancy(vacancy);
    setShowDetailModal(true);
  };

  return (
    <div className="min-h-screen bg-[#05050A]" data-testid="vacancies-page" style={{ fontFamily: "'Inter', sans-serif" }}>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 right-1/4 w-[600px] h-[600px] bg-[#00E5FF]/[0.03] rounded-full blur-[150px]" />
          <div className="absolute bottom-0 left-1/3 w-[500px] h-[500px] bg-[#8A2BE2]/[0.03] rounded-full blur-[120px]" />
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#00E5FF]/20 bg-[#00E5FF]/[0.05] backdrop-blur-md mb-8"
            style={{ animation: 'fadeInUp 0.6s ease-out' }}>
            <Sparkles className="w-3.5 h-3.5 text-[#00E5FF]" />
            <span className="text-xs tracking-[0.2em] uppercase text-[#00E5FF] font-medium">We're Hiring</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-white mb-6"
            style={{ animation: 'fadeInUp 0.6s ease-out 0.1s both' }}>
            Build Your{' '}
            <span className="bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] bg-clip-text text-transparent font-medium">
              Career
            </span>{' '}
            With Us
          </h1>

          <p className="text-base md:text-lg text-white/40 max-w-xl mx-auto leading-relaxed mb-10"
            style={{ animation: 'fadeInUp 0.6s ease-out 0.2s both' }}>
            We're looking for talented individuals who share our passion for excellence in virtual hospitality.
          </p>

          {/* Stats row */}
          <div className="flex justify-center gap-10 md:gap-16"
            style={{ animation: 'fadeInUp 0.6s ease-out 0.3s both' }}>
            {[
              { value: `${vacancies.length}`, label: 'Open Roles' },
              { value: '5', label: 'Establishments' },
              { value: '14+', label: 'Team Members' },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-2xl md:text-3xl font-light bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] bg-clip-text text-transparent">{stat.value}</p>
                <p className="text-[10px] tracking-[0.15em] uppercase text-white/25 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 border-y border-white/[0.03]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/[0.03] mb-5">
              <Star className="w-3 h-3 text-[#F59E0B]" />
              <span className="text-[10px] tracking-[0.2em] uppercase text-white/40 font-medium">Why Join Us</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-light tracking-tight text-white">
              More Than Just a <span className="text-[#00E5FF]">Role</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {BENEFITS.map((benefit, index) => (
              <div key={index} data-testid={`benefit-${index}`}
                className="group p-6 rounded-xl border border-white/[0.04] bg-white/[0.015] hover:bg-white/[0.03] transition-all duration-300 hover:-translate-y-0.5"
                style={{ animation: `fadeInUp 0.4s ease-out ${0.06 * index}s both` }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 border border-white/[0.06]"
                  style={{ backgroundColor: `${benefit.color}10` }}>
                  <benefit.icon className="w-5 h-5" style={{ color: benefit.color }} />
                </div>
                <h3 className="text-white font-medium text-sm mb-1.5 tracking-tight">{benefit.title}</h3>
                <p className="text-white/25 text-xs leading-relaxed">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/[0.03] mb-5">
              <Briefcase className="w-3 h-3 text-[#00E5FF]" />
              <span className="text-[10px] tracking-[0.2em] uppercase text-white/40 font-medium">Open Positions</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-light tracking-tight text-white" data-testid="positions-title">
              Find Your <span className="text-[#00E5FF]">Perfect</span> Role
            </h2>
          </div>

          {/* Search & Filters */}
          <div className="flex flex-col md:flex-row gap-3 mb-8">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#00E5FF]/40" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search positions, department, keyword…"
                data-testid="vacancy-search"
                className="w-full pl-11 pr-4 py-3 bg-[#0A0A12] border border-white/[0.06] rounded-full text-white placeholder-white/25 focus:border-[#00E5FF]/40 focus:ring-2 focus:ring-[#00E5FF]/10 focus:outline-none transition-all text-sm"
              />
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {departments.map(dept => (
                <button key={dept} onClick={() => setActiveFilter(dept)} data-testid={`filter-${dept}`}
                  className={`px-4 py-2.5 rounded-full text-[10px] tracking-[0.12em] uppercase font-medium transition-all whitespace-nowrap border ${
                    activeFilter === dept
                      ? 'bg-[#00E5FF]/[0.1] text-[#00E5FF] border-[#00E5FF]/30'
                      : 'bg-white/[0.02] text-white/40 border-white/[0.06] hover:text-white/70 hover:border-white/[0.12]'
                  }`}>
                  {dept === 'all' ? 'All Roles' : dept}
                </button>
              ))}
            </div>
          </div>

          {/* Vacancy List */}
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 rounded-full border border-white/10 border-t-[#00E5FF] animate-spin" />
            </div>
          ) : filteredVacancies.length === 0 ? (
            <div className="text-center py-20 rounded-2xl border border-white/[0.04] bg-white/[0.01]">
              <Briefcase className="w-14 h-14 mx-auto mb-4 text-white/10" />
              <h3 className="text-lg font-medium text-white mb-2">
                {vacancies.length === 0 ? 'No Openings Right Now' : 'No matching roles found'}
              </h3>
              <p className="text-white/25 text-sm max-w-sm mx-auto">
                {vacancies.length === 0
                  ? 'Check back soon or send us your CV for future opportunities.'
                  : 'Try adjusting your search or filter criteria.'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredVacancies.map((vacancy, index) => (
                <div
                  key={vacancy.id}
                  data-testid={`vacancy-card-${vacancy.id}`}
                  onClick={() => openDetails(vacancy)}
                  className="group relative rounded-2xl border border-white/[0.06] bg-[#0A0A12] hover:border-[#00E5FF]/25 cursor-pointer transition-all duration-500 overflow-hidden hover:-translate-y-1"
                  style={{
                    animation: `fadeInUp 0.5s ease-out ${0.06 * index}s both`,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 0 40px rgba(0,229,255,0.06)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                >
                  {/* Ambient orb */}
                  <div className="pointer-events-none absolute -top-16 -right-24 w-48 h-48 bg-[#00E5FF]/[0.05] rounded-full blur-[80px] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  <div className="relative flex items-center p-5 gap-5">
                    {/* Luxury icon */}
                    <div className="relative flex-shrink-0">
                      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#00E5FF]/[0.08] to-[#8A2BE2]/[0.08] border border-white/[0.08] flex items-center justify-center group-hover:scale-105 transition-transform duration-500"
                        style={{ boxShadow: 'inset 0 0 0 1px rgba(0,229,255,0.08), 0 0 24px -8px rgba(0,229,255,0.15)' }}>
                        <Briefcase className="w-5 h-5 text-[#00E5FF]" strokeWidth={1.5} />
                      </div>
                      {vacancy.is_urgent && (
                        <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full ring-2 ring-[#0A0A12] animate-pulse" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        <h3 className="text-white font-medium text-base tracking-tight group-hover:text-[#00E5FF] transition-colors truncate">
                          {vacancy.title}
                        </h3>
                        {vacancy.is_urgent && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-red-500/10 text-red-300 border border-red-500/25 text-[9px] tracking-[0.08em] font-medium uppercase">
                            <Zap className="w-2.5 h-2.5" />Urgent
                          </span>
                        )}
                        {vacancy.department && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#8A2BE2]/[0.08] text-[#c39be5] border border-[#8A2BE2]/25 text-[9px] tracking-[0.08em] font-medium uppercase">
                            <Layers className="w-2.5 h-2.5" />{vacancy.department}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-[11px] text-white/40">
                        <span className="flex items-center gap-1.5"><MapPin className="w-3 h-3 text-[#00E5FF]/60" />{vacancy.location}</span>
                        <span className="flex items-center gap-1.5"><Clock className="w-3 h-3 text-[#00E5FF]/60" />{vacancy.type}</span>
                        {vacancy.experience_level && (
                          <span className="hidden md:flex items-center gap-1.5"><Award className="w-3 h-3 text-[#00E5FF]/60" />{vacancy.experience_level}</span>
                        )}
                      </div>
                    </div>

                    {/* CTA */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleBookmark(vacancy); }}
                        data-testid={`bookmark-btn-${vacancy.id}`}
                        title={bookmarks.has(vacancy.id) ? 'Remove from saved roles' : 'Save for later'}
                        className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all ${
                          bookmarks.has(vacancy.id)
                            ? 'bg-amber-500/15 border-amber-500/30 text-amber-300 hover:bg-amber-500/25'
                            : 'bg-white/[0.03] border-white/[0.06] text-white/40 hover:text-amber-300 hover:border-amber-500/25'
                        }`}
                      >
                        {bookmarks.has(vacancy.id) ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                      </button>
                      {quickReady?.ready && (
                        <Button
                          onClick={(e) => { e.stopPropagation(); runQuickApply(vacancy); }}
                          disabled={quickBusy === vacancy.id}
                          data-testid={`quick-apply-btn-${vacancy.id}`}
                          title="Apply instantly with your saved CV and profile"
                          className="hidden md:inline-flex bg-gradient-to-br from-emerald-400 to-emerald-600 hover:opacity-90 text-black text-[10px] tracking-[0.12em] uppercase font-semibold px-4 py-2.5 rounded-full transition-all shadow-lg shadow-emerald-500/20 disabled:opacity-60"
                          size="sm"
                        >
                          <Zap className="w-3 h-3 mr-1.5" />
                          {quickBusy === vacancy.id ? 'Sending…' : 'One-click'}
                        </Button>
                      )}
                      <Button
                        onClick={(e) => { e.stopPropagation(); requestApply(vacancy); }}
                        data-testid={`apply-btn-${vacancy.id}`}
                        className={`hidden md:inline-flex ${quickReady?.ready ? 'bg-white/[0.04] border border-white/[0.08] text-white/70 hover:bg-white/[0.08]' : 'bg-gradient-to-br from-[#00E5FF] to-[#0099CC] hover:opacity-90 text-black shadow-lg shadow-[#00E5FF]/15'} text-[10px] tracking-[0.12em] uppercase font-semibold px-5 py-2.5 rounded-full transition-all`}
                        size="sm"
                      >
                        Apply <ArrowRight className="w-3 h-3 ml-1.5" />
                      </Button>
                      <ChevronRight className="w-4 h-4 text-white/15 group-hover:text-[#00E5FF] group-hover:translate-x-0.5 transition-all" />
                    </div>
                  </div>

                  {/* Bottom accent line on hover */}
                  <div className="absolute bottom-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#00E5FF]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
              ))}
            </div>
          )}

          {filteredVacancies.length > 0 && (
            <p className="text-center text-white/15 text-xs mt-6">
              Showing {filteredVacancies.length} of {vacancies.length} position{vacancies.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="relative rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden" data-testid="cta-section">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 bg-[#00E5FF]/[0.04] rounded-full blur-[80px]" />
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-48 h-24 bg-[#8A2BE2]/[0.04] rounded-full blur-[60px]" />

            <div className="relative p-12 md:p-16 text-center">
              <div className="w-14 h-14 bg-gradient-to-br from-[#00E5FF]/10 to-[#8A2BE2]/10 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-white/[0.06]">
                <Mail className="w-6 h-6 text-[#00E5FF]" />
              </div>
              <h2 className="text-xl md:text-2xl font-light tracking-tight text-white mb-3">
                Don't See Your{' '}
                <span className="bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] bg-clip-text text-transparent">Role</span>?
              </h2>
              <p className="text-sm text-white/30 mb-8 max-w-md mx-auto">
                We're always looking for exceptional talent. Send us your CV and tell us how you can contribute.
              </p>
              <a
                href={`mailto:${contactInfo.recruitmentEmail}?subject=General Application - Starnight Holdings`}
                className="inline-flex items-center gap-2 bg-white text-black font-medium text-sm px-7 py-3 rounded-lg hover:bg-white/90 transition-all group"
                data-testid="general-apply-btn"
              >
                <Mail className="w-4 h-4" />
                Send Your CV
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </a>
              <p className="text-white/15 text-xs mt-5">{contactInfo.recruitmentEmail}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Vacancy Details Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="bg-[#0A0A12] border border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl shadow-black/50">
          {selectedVacancy && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  {selectedVacancy.is_urgent && (
                    <Badge className="bg-red-500/10 text-red-400 border-red-500/20 text-[10px]">
                      <Zap className="w-3 h-3 mr-0.5" />Urgent Hiring
                    </Badge>
                  )}
                  <Badge className="bg-[#00E5FF]/10 text-[#00E5FF] border-[#00E5FF]/20 text-[10px]">
                    {selectedVacancy.department}
                  </Badge>
                </div>
                <DialogTitle className="text-xl font-medium text-white tracking-tight">
                  {selectedVacancy.title}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                {/* Quick Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                  {[
                    { icon: MapPin, label: 'Location', value: selectedVacancy.location, color: '#00E5FF' },
                    { icon: Clock, label: 'Type', value: selectedVacancy.type, color: '#8A2BE2' },
                    { icon: Award, label: 'Level', value: selectedVacancy.experience_level || 'All Levels', color: '#F59E0B' },
                    { icon: Calendar, label: 'Posted', value: selectedVacancy.posted_date || 'Recently', color: '#10B981' },
                  ].map((info, i) => (
                    <div key={i} className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.04] text-center">
                      <info.icon className="w-4 h-4 mx-auto mb-1.5" style={{ color: info.color }} />
                      <p className="text-[10px] text-white/25 uppercase tracking-wider">{info.label}</p>
                      <p className="text-xs text-white font-medium mt-0.5">{info.value}</p>
                    </div>
                  ))}
                </div>

                {/* Deadline */}
                {selectedVacancy.application_deadline && (
                  <div className="flex items-center gap-3 p-3.5 rounded-xl bg-amber-500/5 border border-amber-500/15">
                    <Calendar className="w-4 h-4 text-amber-400 flex-shrink-0" />
                    <div>
                      <p className="text-[10px] text-amber-400/60 uppercase tracking-wider">Deadline</p>
                      <p className="text-xs text-amber-400 font-medium">{selectedVacancy.application_deadline}</p>
                    </div>
                  </div>
                )}

                {/* Salary */}
                {selectedVacancy.salary_range && (
                  <div className="flex items-center gap-3 p-3.5 rounded-xl bg-emerald-500/5 border border-emerald-500/15">
                    <Target className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <div>
                      <p className="text-[10px] text-emerald-400/60 uppercase tracking-wider">Salary Range</p>
                      <p className="text-xs text-emerald-400 font-medium">{selectedVacancy.salary_range}</p>
                    </div>
                  </div>
                )}

                {/* Description */}
                <div>
                  <h3 className="text-sm font-medium text-white mb-2.5 flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-[#00E5FF]" />About This Role
                  </h3>
                  <p className="text-xs text-white/35 leading-relaxed">{selectedVacancy.description}</p>
                </div>

                {/* Responsibilities */}
                {selectedVacancy.responsibilities?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-white mb-2.5 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />Responsibilities
                    </h3>
                    <ul className="space-y-2">
                      {selectedVacancy.responsibilities.map((item, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-xs text-white/35">
                          <span className="w-1 h-1 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />{item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Requirements */}
                {selectedVacancy.requirements?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-white mb-2.5 flex items-center gap-2">
                      <Award className="w-4 h-4 text-[#8A2BE2]" />Requirements
                    </h3>
                    <ul className="space-y-2">
                      {selectedVacancy.requirements.map((item, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-xs text-white/35">
                          <span className="w-1 h-1 rounded-full bg-[#8A2BE2] mt-1.5 flex-shrink-0" />{item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Benefits */}
                {selectedVacancy.benefits?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-white mb-2.5 flex items-center gap-2">
                      <Star className="w-4 h-4 text-amber-400" />What We Offer
                    </h3>
                    <ul className="space-y-2">
                      {selectedVacancy.benefits.map((item, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-xs text-white/35">
                          <span className="w-1 h-1 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" />{item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Apply Actions */}
                <div className="pt-4 border-t border-white/[0.04] flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={() => { setShowDetailModal(false); requestApply(selectedVacancy); }}
                    className="flex-1 bg-gradient-to-br from-[#00E5FF] to-[#0099CC] text-black hover:opacity-90 font-semibold text-[11px] tracking-[0.12em] uppercase py-3 rounded-full shadow-lg shadow-[#00E5FF]/15"
                    data-testid="modal-apply-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Apply for This Role
                  </Button>
                  <Button variant="outline" onClick={() => setShowDetailModal(false)}
                    className="border-white/10 text-white/40 hover:text-white/70 rounded-full px-6 text-[11px] tracking-[0.12em] uppercase">
                    Close
                  </Button>
                </div>
                <p className="text-center text-white/15 text-[10px]">
                  Or email us directly at {contactInfo.recruitmentEmail}
                </p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Bottom gradient */}
      <div className="h-px bg-gradient-to-r from-transparent via-white/5 to-transparent" />

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* In-app application modal */}
      {applyingFor && (
        <VacancyApplicationModal vacancy={applyingFor} onClose={() => setApplyingFor(null)} currentUser={currentUser} />
      )}

      {/* Sign-in / Sign-up prompt before applying */}
      <Dialog open={!!authPromptFor} onOpenChange={(v) => !v && setAuthPromptFor(null)}>
        <DialogContent className="bg-[#0A0A14] border border-white/[0.08] text-white max-w-md rounded-3xl p-0 overflow-hidden" data-testid="apply-auth-prompt">
          <div className="relative px-7 pt-7 pb-5 border-b border-white/[0.06]">
            <div className="absolute inset-0 bg-gradient-to-br from-[#00E5FF]/[0.06] via-transparent to-[#8A2BE2]/[0.05] pointer-events-none" />
            <div className="relative flex items-start gap-4">
              <div className="w-11 h-11 rounded-2xl bg-[#00E5FF]/15 border border-[#00E5FF]/30 flex items-center justify-center flex-shrink-0">
                <Lock className="w-5 h-5 text-[#00E5FF]" />
              </div>
              <div>
                <DialogTitle className="text-white text-base font-medium">Sign in to apply</DialogTitle>
                <p className="text-white/50 text-xs mt-1 leading-relaxed">
                  Applications live inside your Starnight account so you can track status, receive updates and reuse your details next time.
                </p>
              </div>
            </div>
          </div>
          {authPromptFor && (
            <div className="px-7 py-4 border-b border-white/[0.04]">
              <p className="text-[10px] tracking-[0.18em] uppercase text-white/30 font-medium mb-1">Applying for</p>
              <p className="text-white text-sm font-medium">{authPromptFor.title}</p>
              <p className="text-white/40 text-[11px] mt-0.5">{authPromptFor.department} · {authPromptFor.location || 'Remote'}</p>
            </div>
          )}
          <div className="px-7 py-5 space-y-2.5">
            <ul className="space-y-2 mb-2">
              {[
                'Track every application from your Profile Hub',
                'Get inbox alerts the moment your status changes',
                'Autofill next time so applying takes seconds',
              ].map((b, i) => (
                <li key={i} className="flex items-center gap-2.5 text-white/60 text-xs">
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-300 flex-shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <Button
              onClick={() => navigate('/login?next=' + encodeURIComponent('/vacancies'))}
              data-testid="apply-signin-btn"
              className="w-full bg-white text-black hover:bg-white/90 font-medium py-3 rounded-xl text-sm">
              <LogIn className="w-4 h-4 mr-2" /> Sign in
            </Button>
            <Button
              onClick={() => navigate('/signup?next=' + encodeURIComponent('/vacancies'))}
              data-testid="apply-signup-btn"
              className="w-full bg-white/[0.04] text-white border border-white/[0.08] hover:bg-white/[0.08] font-medium py-3 rounded-xl text-sm">
              <UserPlus className="w-4 h-4 mr-2" /> Create an account
            </Button>
            <p className="text-white/25 text-[10px] text-center pt-1">
              We'll bring you right back to this vacancy after you're in.
            </p>
          </div>
        </DialogContent>
      </Dialog>
      )}
    </div>
  );
};

export default Vacancies;
